// A two-step inline confirm button (Session AR Item 3, the platform-wide delete standard for simple
// in-place deletes). The first click switches the button to a red "Confirm?" state; a second click
// executes the action. It resets on blur (clicking anywhere else), on Escape, or after a short
// timeout, so a stray first click never lingers as an armed delete. No modal, no typing.
//
// Use this for a delete that carries NO dependency blockers to surface (a document, a part record, a
// van-stock item, a sub-record). An entity-level delete that must show a 409 blocker summary uses the
// shared ConfirmDeleteModal instead.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactNode } from "react";

export function ConfirmButton({
  onConfirm,
  label = "Delete",
  confirmLabel = "Confirm?",
  busy = false,
  busyLabel = "Deleting...",
  className = "",
  armedClassName = "!text-ele-danger font-semibold",
  disabled = false,
  title,
  resetMs = 4000,
  style,
  armedNote,
}: {
  onConfirm: () => void;
  label?: string;
  confirmLabel?: string;
  busy?: boolean;
  busyLabel?: string;
  // The button's own styling in the idle state (padding, border, colour). The armed state appends
  // armedClassName (a red text override by default) so the confirm state is unmistakable.
  className?: string;
  armedClassName?: string;
  disabled?: boolean;
  title?: string;
  resetMs?: number;
  // Inline styling for callers whose button palette is inline (the AAG colour-coded quote-row
  // actions). Session AAV.
  style?: CSSProperties;
  // Session SC9B: a note shown beside the button ONLY while it is armed (the account-on-stop warning). It carries its own box and renders nothing
  // when there is nothing to say. Absent: the button renders exactly as before, with no wrapper. It never disables or changes the confirm.
  armedNote?: ReactNode;
}) {
  const [armed, setArmed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ bottom: number; right: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearTimer() {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }

  useEffect(() => clearTimer, []);

  function disarm() {
    setArmed(false);
    clearTimer();
  }

  function handleClick() {
    if (busy || disabled) return;
    if (!armed) {
      const r = buttonRef.current?.getBoundingClientRect();
      if (r) setAnchor({ bottom: r.bottom, right: r.right });
      setArmed(true);
      clearTimer();
      // Auto-reset after a few seconds so a forgotten armed state never lingers as a live delete.
      timer.current = setTimeout(() => setArmed(false), resetMs);
      return;
    }
    disarm();
    onConfirm();
  }

  const button = (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      onBlur={disarm}
      onKeyDown={(e) => {
        if (e.key === "Escape") disarm();
      }}
      disabled={busy || disabled}
      title={title}
      style={style}
      aria-label={armed ? `${confirmLabel} (${label})` : label}
      // Mobile floor baked in ahead of the caller's className so every ConfirmButton reaches the
      // 44px tap target on phones regardless of the (often zero-padding) className each call site
      // supplies; desktop is unaffected since these are all max-sm: utilities.
      className={`max-sm:inline-flex max-sm:min-h-11 max-sm:items-center ${className} ${armed ? armedClassName : ""}`.trim()}
    >
      {busy ? busyLabel : armed ? confirmLabel : label}
    </button>
  );
  if (armedNote === undefined) return button;
  // The note is drawn in a portal on the page, fixed under the armed button and right-aligned to it, so a table cell or a menu with
  // overflow hidden can never clip it, and the button keeps its own place in the layout (no wrapper element at all). The portal box carries no
  // styling of its own: a note that has nothing to say renders nothing, so nothing is drawn.
  return (
    <>
      {button}
      {armed && anchor &&
        createPortal(
          <div style={{ position: "fixed", top: anchor.bottom + 4, right: Math.max(8, window.innerWidth - anchor.right), zIndex: 70 }} className="w-64 max-w-[calc(100vw-1rem)]">
            {armedNote}
          </div>,
          document.body,
        )}
    </>
  );
}
